import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import html2canvas from 'html2canvas';
import Dropdown from 'components/shared/Dropdown/Dropdown';
import { IconLoader } from 'common/IconLoader';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import { createFileName, convertJSONtoCSV } from '../utils/helpers';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Status } from 'common/utils/constants';
import '../HeatmapViewAndExportModal.css';

const HeatmapExportDropdown = (props) => {
  const [isExportDropdownOpen, setIsExportDropdownOpen] = useState(false);
  const [selectedExportType, setSelectedExportType] = useState(null);

  const userTag = useSelector((store) => store.data?.user?.userTag);
  const selectedRastersDictionary = useSelector((store) => store.ui.singleDevicePage.heatmap.selectedRastersDictionary);
  const deviceHeatmapHistoryDictionary = useSelector(
    (store) => store.data.heatmapHistoryDictionary?.[props.donorDevice]
  );
  const exportedSelectedOptions = ['Image', 'Raw Data', 'JSON', 'Export All'];

  const getAnyRasterRecordStatusName = 'GET-AnyRasterRecord';
  const getAnyRasterRecordStatus = useSelector((store) => store.statuses[getAnyRasterRecordStatusName]);
  const isFetching = getAnyRasterRecordStatus === Status.PENDING;
  const jsonExportInterval = useRef();
  const csvExportInterval = useRef();
  const imageExportInterval = useRef();
  const allExportInterval = useRef();
  const allZip = new JSZip();

  let numOfSelectedRasters = 0;
  selectedRastersDictionary &&
    Object.keys(selectedRastersDictionary).forEach((rasterId) => {
      if (selectedRastersDictionary[rasterId] === true) numOfSelectedRasters += 1;
    });

  const generateImageFileNameAndData = async () => {
    const imageFileNameAndData = {};
    let fileIdx = 1;
    selectedRastersDictionary &&
      Object.keys(selectedRastersDictionary).map(async (rasterId) => {
        if (selectedRastersDictionary[rasterId]) {
          let record = await deviceHeatmapHistoryDictionary[rasterId];
          if (record.rasterId === rasterId) {
            let fileName = createFileName(record);
            if (imageFileNameAndData.hasOwnProperty(fileName)) {
              fileName += `(${fileIdx})`;
              fileIdx++;
            }
            imageFileNameAndData[fileName] = record;
          }
        }
      });
    return imageFileNameAndData;
  };

  const handleSingleImageDownload = async (isExportAll = false) => {
    let imageFileNameAndData = await generateImageFileNameAndData();
    let fileName = Object.keys(imageFileNameAndData)?.[0];
    let record = fileName && imageFileNameAndData[fileName];

    const element = document.getElementById(`HeatmapViewAndExportModal_HeatMapDiv_${record.rasterId}`);
    const canvas = await html2canvas(element);
    const dataURL = canvas.toDataURL('image/jpg');
    if (isExportAll) {
      allZip.file(`${fileName}.png`, dataURL?.slice(22), {
        base64: true
      });
      return;
    }
    const link = document.createElement('a');

    link.href = dataURL;
    link.download = `${fileName}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    props.setIsCollectingFiles(false);
  };

  const handleMultipleImagesDownload = async (isExportAll = false) => {
    const imageZip = isExportAll ? allZip.folder('Image Files') : new JSZip();
    let imageFileNameAndData = await generateImageFileNameAndData();

    Object.entries(imageFileNameAndData).map(async ([fileName, record]) => {
      let elementId = `HeatmapViewAndExportModal_HeatMapDiv_${record.rasterId}`;
      const element = document.getElementById(elementId);
      const canvas = await html2canvas(element);

      // slice(22) is to remove the 'data:image/png;base64,' from the dataURL
      // in order to get JSZip to correctly save dataURL to valid png file
      const data = canvas.toDataURL('image/jpg')?.slice(22);
      await imageZip.file(fileName + '.png', data, {
        base64: true
      });
      element.remove();
    });

    let intervalAction = () => {
      props.setImageExportClick(false);
    };

    if (!isExportAll) {
      generateZipFile(imageZip, 'png', imageExportInterval, intervalAction);
    }
  };

  const handleDownloadImage = async (isExportAll = false) => {
    await props.setImageExportClick(true);
    if (numOfSelectedRasters === 1) {
      handleSingleImageDownload(isExportAll);
    } else {
      handleMultipleImagesDownload(isExportAll);
    }
  };

  const generateCSVFileNameAndData = (csvRawDataArray) => {
    const csvFileNameAndData = {};
    csvRawDataArray &&
      csvRawDataArray.forEach((data, idx) => {
        const csvData = data.csvData;
        let csv = convertJSONtoCSV(csvData, userTag);
        let fileName = createFileName(data.record);
        if (csvFileNameAndData.hasOwnProperty(fileName)) {
          fileName += `(${idx})`;
        }
        csvFileNameAndData[fileName] = csv;
      });
    return csvFileNameAndData;
  };

  const handleSingleCSVDownload = (csvRawData, isExportAll = false) => {
    const csvData = csvRawData?.csvData;
    const record = csvRawData?.record;
    const csv = convertJSONtoCSV(csvData, userTag);
    const fileName = createFileName(record);
    const csvDataString = `data:text/json;chatset=utf-8,${encodeURIComponent(csv)}`;

    if (isExportAll) {
      allZip.file(`${fileName}.csv`, csv);
      return;
    }

    const link = document.createElement('a');
    link.href = csvDataString;
    link.download = `${fileName}.csv`;

    link.click();
    props.setIsCollectingFiles(false);
  };

  const handleMultipleCSVsDownload = (csvRawDataArray, isExportAll = false) => {
    const csvZip = isExportAll ? allZip.folder('CSV Files') : new JSZip();
    const csvFileNameAndData = generateCSVFileNameAndData(csvRawDataArray);
    Object.entries(csvFileNameAndData).forEach(([fileName, csv]) => {
      csvZip.file(`${fileName}.csv`, csv);
    });

    if (!isExportAll) {
      generateZipFile(csvZip, 'csv', csvExportInterval);
    }
  };

  const handleDownloadRawData = (isExportAll = false) => {
    const csvRawDataArray = props.cvsDataReformatter();

    if (csvRawDataArray.length === 1) {
      handleSingleCSVDownload(csvRawDataArray[0], isExportAll);
    } else {
      handleMultipleCSVsDownload(csvRawDataArray, isExportAll);
    }
  };

  const generateJSONFileNameAndData = () => {
    const jsonDataArray = [];
    const jsonFileNameAndData = {};
    selectedRastersDictionary &&
      Object.keys(selectedRastersDictionary).forEach((rasterId, idx) => {
        if (selectedRastersDictionary[rasterId]) {
          let record = deviceHeatmapHistoryDictionary?.[rasterId];

          if (record.rasterId === rasterId) {
            let fileName = createFileName(record);
            if (jsonFileNameAndData.hasOwnProperty(fileName)) {
              fileName += `(${idx})`;
            }
            jsonFileNameAndData[fileName] = record;
            jsonDataArray.push({ ...record, user: userTag || 'Unknown' });
          }
        }
      });
    return jsonDataArray;
  };

  const handleSingleJSONDownload = (jsonRawData, isExportAll = false) => {
    let record = jsonRawData;
    let fileName = createFileName(record);
    const jsonString = `data:text/json;chatset=utf-8,${encodeURIComponent(JSON.stringify(record, null, 4))}`;

    if (isExportAll) {
      allZip.file(fileName + '.json', JSON.stringify(record, null, 4), {
        binary: true
      });
      return;
    }

    const link = document.createElement('a');
    link.href = jsonString;
    link.download = `${fileName}.json`;

    link.click();
    props.setIsCollectingFiles(false);
  };

  const handleMultipleJSONsDownload = async (jsonRawData, isExportAll = false) => {
    const jsonZip = isExportAll ? allZip.folder('JSON Files') : new JSZip();
    let idx = 1;
    let existingFileNames = {};
    await jsonRawData.forEach((record) => {
      let fileName = createFileName(record);
      if (existingFileNames.hasOwnProperty(fileName)) {
        fileName += `(${idx})`;
        idx++;
      }
      existingFileNames[fileName] = true;
      jsonZip.file(fileName + '.json', JSON.stringify(record, null, 4), {
        binary: true
      });
    });

    if (!isExportAll) {
      generateZipFile(jsonZip, 'json', jsonExportInterval);
    }
  };

  const handleDownloadJSON = (isExportAll = false) => {
    let jsonDataArray = generateJSONFileNameAndData();

    if (jsonDataArray.length === 1) {
      handleSingleJSONDownload(jsonDataArray[0], isExportAll);
    } else {
      handleMultipleJSONsDownload(jsonDataArray, isExportAll);
    }
  };

  const generateZipFile = (zip, fileType, exportInterval, intervalAction) => {
    exportInterval.current = setInterval(() => {
      if (zip && zip.files && Object.keys(zip.files).length === numOfSelectedRasters) {
        zip.generateAsync({ type: 'blob' }).then(function (content) {
          if (intervalAction) intervalAction();
          props.setImageExportClick(false);
          saveAs(content, `${props.donorDevice}-${fileType}-files.zip`);
        });
        clearInterval(exportInterval.current);
        props.setIsCollectingFiles(false);
      }
    }, 500);
  };

  const handleExportAll = () => {
    allExportInterval.current = setInterval(() => {
      if (
        allZip &&
        allZip.files &&
        Object.keys(allZip.files).length === (numOfSelectedRasters === 1 ? 3 : 3 + 3 * numOfSelectedRasters)
      ) {
        allZip.generateAsync({ type: 'blob' }).then(function (content) {
          props.setImageExportClick(false);
          saveAs(content, `${props.donorDevice}-all-files.zip`);
        });
        clearInterval(allExportInterval.current);
        props.setIsCollectingFiles(false);
      }
    }, 500);
  };

  const handleExportSelection = async (selection) => {
    await props.setIsCollectingFiles(true);
    let selectText = selection?.props?.children?.props?.text
      ? selection.props.children.props.text
      : selection.props.children;
    setSelectedExportType(selectText);
    switch (selectText) {
      case 'Image':
        handleDownloadImage();
        break;
      case 'Raw Data':
        handleDownloadRawData();
        break;
      case 'JSON':
        handleDownloadJSON();
        break;
      case 'Export All':
        let isExportAll = true;
        handleDownloadRawData(isExportAll);
        handleDownloadJSON(isExportAll);
        handleDownloadImage(isExportAll);

        handleExportAll();
        break;
      default:
        return;
    }
  };

  return (
    <div
      id="HeatmapViewAndExportModal_ExportSelectedDropdown_Container"
      className={classnames({
        HeatmapViewAndExportModal_ExportSelectedDropdown_Container: true,
        HeatmapViewAndExportModal_ExportSelectedDropdown_Container_Disabled: numOfSelectedRasters === 0 || isFetching
      })}
      onClick={() => setIsExportDropdownOpen(!isExportDropdownOpen)}
    >
      <Dropdown
        hideArrow={true}
        contentUpwards={true}
        selectItem={async (selection) => await handleExportSelection(selection)}
        data={exportedSelectedOptions.map((option, idx) => {
          return (
            <div
              className="HeatmapViewAndExportModal_ExportSelectedDropdown_Option"
              id={`HeatmapViewAndExportModal_ExportSelectedDropdown_Option${idx}`}
            >
              {option === 'Raw Data' ? option : option}
            </div>
          );
        })}
        customClose={() => setIsExportDropdownOpen(false)}
        customButtonContent={
          <div
            id="HeatmapViewAndExportModal_ExportSelectedDropdown_Button"
            className={classnames({
              HeatmapViewAndExportModal_ExportSelectedDropdown_Button: true
            })}
          >
            <div
              id="HeatmapViewAndExportModal_ExportSelectedDropdown_Label"
              className={classnames({
                HeatmapViewAndExportModal_ExportSelectedDropdown_Label: true,
                HeatmapViewAndExportModal_ExportSelectedDropdown_Label_Selected: selectedExportType
              })}
            >
              {selectedExportType ?? 'Export Selected'}
            </div>
            <div
              id="HeatmapViewAndExportModal_ExportSelectedDropdown_Arrow"
              className="HeatmapViewAndExportModal_ExportSelectedDropdown_Arrow"
            >
              <SvgAF img={IconLoader.icon_split_default} hover={IconLoader.icon_split_hover} />
            </div>
          </div>
        }
      />
    </div>
  );
};

export default React.memo(HeatmapExportDropdown);
